import {
  ClientOnly,
  HeadContent,
  Outlet,
  Scripts,
  createRootRoute,
  useRouterState,
} from "@tanstack/react-router"
import { Annotations } from "@ui/components/Annotations"
import { CookieBanner } from "@ui/components/CookieBanner"
import { useSystemFavicon } from "@ui/hooks/useSystemFavicon"
import {
  CookieConsentProvider,
  getInitialPostHogCookielessMode,
  getInitialPostHogPersistence,
  syncInitialPostHogConsent,
  syncPostHogConsent,
} from "@ui/lib/cookieConsent"
import { faviconLinks } from "@ui/lib/favicon"
import {
  DEFAULT_SOCIAL_DESCRIPTION,
  DEFAULT_SOCIAL_IMAGE_ALT,
  DEFAULT_SOCIAL_TITLE,
  seoMeta,
} from "@ui/lib/seo"
import { ThemeProvider, themeHydrationScript } from "@ui/lib/theme"
import { useCookieConsent } from "@ui/lib/useCookieConsent"
import { PostHogProvider, usePostHog } from "posthog-js/react"
import { useEffect } from "react"

import { NotFoundPage } from "@/components/not-found/NotFoundPage"
import { PublicLayout } from "@/components/PublicLayout"

import appCss from "../styles.css?url"

// A missing page must not describe itself as the homepage: no canonical or
// og:url, and kept out of the index.
const notFoundMeta = [
  { title: "Page not found · Entire" },
  {
    name: "description",
    content:
      "The page you're looking for doesn't exist, has been moved, or requires you to log in.",
  },
  { name: "robots", content: "noindex" },
]

export const Route = createRootRoute({
  // A loader's notFound() renders at the root boundary (flagging this match);
  // an unmatched path flags the deepest matched layout instead.
  head: ({ match, matches }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      ...(match.globalNotFound || matches.some((m) => m.globalNotFound || m.status === "notFound")
        ? notFoundMeta
        : seoMeta({
            title: DEFAULT_SOCIAL_TITLE,
            description: DEFAULT_SOCIAL_DESCRIPTION,
            url: "https://entire.com",
            imageAlt: DEFAULT_SOCIAL_IMAGE_ALT,
          }).meta),
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      {
        rel: "preload",
        href: "/fonts/entire-headline-semibold.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        href: "/fonts/entire-mono-regular.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      ...faviconLinks,
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Entire",
          url: "https://entire.com",
          logo: "https://entire.com/favicon.svg",
        }),
      },
    ],
  }),
  component: RootComponent,
  shellComponent: RootDocument,
  notFoundComponent: NotFoundPage,
})

function hydrateSessionHint() {
  try {
    const match = document.cookie.match(/(?:^|; )entire_website_session_hint=([^;]+)/)
    document.documentElement.dataset["hasSession"] = match?.[1] === "true" ? "true" : "false"
  } catch {}
}

const sessionHintScript = `(${hydrateSessionHint.toString()})()`

function RootDocument({ children }: { children: React.ReactNode }) {
  useSystemFavicon()
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <script dangerouslySetInnerHTML={{ __html: themeHydrationScript }} />
        <script dangerouslySetInnerHTML={{ __html: sessionHintScript }} />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  )
}

function PostHogConsentSync({ children }: { children: React.ReactNode }) {
  const { consentStatus } = useCookieConsent()
  const posthog = usePostHog()

  useEffect(() => {
    if (!posthog?.config.token) return
    syncPostHogConsent(posthog, consentStatus)
  }, [consentStatus, posthog])

  return <>{children}</>
}

// Keep PostHog loaded in dev so hooks like usePostHog work, but opt out of all
// capturing (events, exceptions, recordings) so local errors never reach
// PostHog. Mirrors frontend/src/app/providers.tsx.
const isDev = import.meta.env.DEV

function RootComponent() {
  const isTrailsEmbed = useRouterState({
    select: (state) => state.location.pathname.replace(/\/$/, "") === "/features/trails/demo",
  })
  // Embedded product previews own their viewport and portals. Keep marketing
  // chrome, consent banners, annotations, and duplicate analytics outside it.
  if (isTrailsEmbed)
    return (
      <ThemeProvider>
        <Outlet />
      </ThemeProvider>
    )

  const content = (
    <>
      <PublicLayout>
        <Outlet />
      </PublicLayout>
      <ClientOnly>
        <CookieBanner />
        <Annotations />
      </ClientOnly>
    </>
  )

  return (
    <ThemeProvider>
      <CookieConsentProvider>
        <PostHogProvider
          apiKey={import.meta.env["VITE_PUBLIC_POSTHOG_KEY"]}
          options={{
            api_host: import.meta.env["VITE_PUBLIC_POSTHOG_API_HOST"],
            ui_host: import.meta.env["VITE_PUBLIC_POSTHOG_UI_HOST"],
            defaults: "2025-05-24",
            capture_exceptions: !isDev,
            debug: isDev,
            persistence: getInitialPostHogPersistence(),
            cookieless_mode: isDev ? undefined : getInitialPostHogCookielessMode(),
            opt_out_capturing_by_default: isDev,
            disable_session_recording: isDev,
            loaded: isDev ? undefined : syncInitialPostHogConsent,
          }}
        >
          {isDev ? content : <PostHogConsentSync>{content}</PostHogConsentSync>}
        </PostHogProvider>
      </CookieConsentProvider>
    </ThemeProvider>
  )
}
